
import React, { useState } from 'react';
import BellIcon from './icons/BellIcon';
import MenuIcon from './icons/MenuIcon';
import CartIcon from './icons/CartIcon';
import HouseholdIcon from './icons/HouseholdIcon';
import SideMenu from './SideMenu';
import type { User, Household, Recipe } from '../types';
import { getTranslation } from '../utils/translations';
import { isGuidedUser } from '../utils/accountMode';
import FastingHeaderIndicator from './fasting/FastingHeaderIndicator';
import FastingStartButton from './fasting/FastingStartButton';

interface HeaderProps {
    user: User;
    authUser?: User | null;
    isImpersonating?: boolean;
    onExitImpersonation?: () => void;
    activeHouseholdId?: string;
    households: Household[];
    recipes: Recipe[];
    onSwitchHousehold: (householdId: string) => void;
    onLogout: () => void;
    onUserManagementClick: () => void;
    onHouseholdManagementClick: () => void;
    onRecipeSelect: (recipe: Recipe) => void;
    onPersonalDetailsClick: () => void;
    onSettingsClick: () => void;
    onSubscriptionManagementClick?: () => void;
    onTosClick: () => void;
    onContactClick: () => void;
    onHowToClick: () => void;
    onSuggestionBoxClick: () => void;
    onConsultationBoxClick?: () => void;
    onSuggestionManagementClick: () => void;
    onChefManagementClick?: () => void;
    onGuidedClientsClick?: () => void;
    onPhraseOfDayAdminClick?: () => void;
    onOpenReminders?: () => void;
    onOpenActiveCart?: () => void;
    hasReminders?: boolean;
    onTabChange: (tab: 'plan' | 'recipes' | 'lists' | 'monthly') => void;
    showFastingUi?: boolean;
    fastingActive?: boolean;
    fastingScheduled?: boolean;
    fastingRemainingMs?: number;
    onOpenFastingTrackerModal?: () => void;
    pendingSubscriptionCount?: number;
    newSuggestionsCount?: number;
    unreadNotificationCount?: number;
    onNotificationsClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ 
    user, authUser, isImpersonating = false, onExitImpersonation, activeHouseholdId, households, recipes, onSwitchHousehold, onLogout, onUserManagementClick,
    onHouseholdManagementClick, onRecipeSelect, onPersonalDetailsClick,
    onSettingsClick, onSubscriptionManagementClick, onTosClick, onContactClick, onHowToClick, onSuggestionBoxClick,
    onSuggestionManagementClick, onChefManagementClick, onGuidedClientsClick, onPhraseOfDayAdminClick,
    onConsultationBoxClick,
    onOpenReminders, onOpenActiveCart, hasReminders = false, onTabChange,
    showFastingUi = false, fastingActive = false, fastingScheduled = false, fastingRemainingMs = 0, onOpenFastingTrackerModal,
    pendingSubscriptionCount = 0, newSuggestionsCount = 0,
    unreadNotificationCount = 0, onNotificationsClick,
}) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(user.language, key);
    const guided = isGuidedUser(user);

    const activeHousehold = households.find(h => h.id === activeHouseholdId);

    return (
        <>
            <header className="min-h-16 flex-shrink-0 px-3 sm:px-4 pt-[max(0.5rem,env(safe-area-inset-top))] pb-2 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md sticky top-0 z-50 border-b border-gray-50 dark:border-slate-800 shadow-sm grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-1 sm:gap-2 font-rubik transition-all">
                {/* Start: menu — never shrink below touch target */}
                <div className="flex justify-start shrink-0">
                    <button 
                        id="header-menu-button"
                        onClick={() => setIsMenuOpen(true)}
                        className="shrink-0 flex items-center justify-center min-w-11 min-h-11 p-2 text-slate-400 dark:text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition-all rounded-full hover:bg-slate-50 dark:hover:bg-slate-800 active:scale-95"
                        aria-label={t('menu')}
                    >
                        <MenuIcon className="w-6 h-6 shrink-0" />
                    </button>
                </div>

                {/* Center: household — absorbs horizontal pressure */}
                <div className="flex justify-center min-w-0 px-0.5 sm:px-1 overflow-hidden">
                    <button 
                        id="header-household-button"
                        onClick={() => { if (!guided) onHouseholdManagementClick(); }}
                        className={`flex items-center justify-center gap-2 h-9 w-9 min-[400px]:w-auto px-0 min-[400px]:px-3 bg-gray-50 dark:bg-slate-800 rounded-full text-slate-600 dark:text-slate-300 transition-all border border-gray-100 dark:border-slate-700 ${guided ? 'opacity-80 cursor-default' : 'hover:bg-gray-100 dark:hover:bg-slate-700'}`}
                        title={activeHousehold?.name || t('personalHouseholdName')}
                    >
                        <HouseholdIcon className="w-4 h-4 opacity-50 shrink-0" />
                        <span className="hidden min-[400px]:inline text-xs font-bold uppercase tracking-wider truncate">
                            {activeHousehold?.name || t('personalHouseholdName')}
                        </span>
                    </button>
                </div>

                {/* End: fasting, notifications & cart */}
                <div className="flex items-center justify-end gap-0.5 sm:gap-2 shrink-0 min-w-0">
                    {showFastingUi && (
                        <div id="header-fasting-indicator" className="flex items-center shrink min-w-0">
                            {fastingActive && onOpenFastingTrackerModal ? (
                                <FastingHeaderIndicator
                                    remainingMs={fastingRemainingMs}
                                    language={user.language}
                                    onClick={onOpenFastingTrackerModal}
                                />
                            ) : fastingScheduled && onOpenFastingTrackerModal ? (
                                <FastingHeaderIndicator
                                    remainingMs={fastingRemainingMs}
                                    language={user.language}
                                    scheduled={true}
                                    onClick={onOpenFastingTrackerModal}
                                />
                            ) : onOpenFastingTrackerModal ? (
                                <FastingStartButton
                                    language={user.language}
                                    onClick={onOpenFastingTrackerModal}
                                />
                            ) : null}
                        </div>
                    )}
                    <button 
                        onClick={onOpenReminders}
                        className={`shrink-0 flex items-center justify-center min-w-10 min-h-10 p-2 transition-all rounded-full ${
                            hasReminders
                                ? 'text-sky-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20'
                                : 'text-slate-300 dark:text-slate-600 hover:text-slate-600 dark:hover:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                        }`}
                        title={hasReminders ? t('reminders') : undefined}
                    >
                        <BellIcon className="w-6 h-6" />
                    </button>
                    <button 
                        id="header-active-cart-button"
                        onClick={onOpenActiveCart}
                        className="shrink-0 flex items-center justify-center min-w-10 min-h-10 p-2 text-brand-orange hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all rounded-full active:scale-95"
                        title={t('activeCart')}
                    >
                        <CartIcon className="w-7 h-7 stroke-[2px]" />
                    </button>
                </div>
            </header>

            <SideMenu 
                isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} user={user} authUser={authUser} isImpersonating={isImpersonating} onExitImpersonation={onExitImpersonation} activeHouseholdId={activeHouseholdId} households={households} recipes={recipes}
                onSwitchHousehold={onSwitchHousehold} onLogout={onLogout} onUserManagementClick={onUserManagementClick}
                onHouseholdManagementClick={onHouseholdManagementClick} onRecipeSelect={onRecipeSelect}
                onPersonalDetailsClick={onPersonalDetailsClick} onSettingsClick={onSettingsClick}
                onSubscriptionManagementClick={onSubscriptionManagementClick} onTosClick={onTosClick} onContactClick={onContactClick}
                onHowToClick={onHowToClick} onSuggestionBoxClick={onSuggestionBoxClick} onConsultationBoxClick={onConsultationBoxClick} onSuggestionManagementClick={onSuggestionManagementClick}
                onChefManagementClick={onChefManagementClick}
                onGuidedClientsClick={onGuidedClientsClick}
                onPhraseOfDayAdminClick={onPhraseOfDayAdminClick}
                onTabChange={onTabChange}
                pendingSubscriptionCount={pendingSubscriptionCount}
                newSuggestionsCount={newSuggestionsCount}
                unreadNotificationCount={unreadNotificationCount}
                onNotificationsClick={onNotificationsClick}
            />
        </>
    );
};

export default Header;
